'use client';

// Fork of https://github.com/radix-ui/primitives/blob/main/packages/react/roving-focus/src/RovingFocusGroup.tsx
//
// Differences:
// - Enforce tabIndex={-1} for non-focusable items (1)
// - Allow roving focus even when row contents are focused (2)
// - Don't prevent default onMouseDown to allow text selection (3)

import { Direction } from 'radix-ui';
import {
  Collection,
  composeEventHandlers,
  Context,
  Primitive,
  useCallbackRef,
  useComposedRefs,
  useControllableState,
} from 'radix-ui/internal';
import * as React from 'react';

const ENTRY_FOCUS = 'rovingFocusGroup.onEntryFocus';
const EVENT_OPTIONS = { bubbles: false, cancelable: true };

// -----------------------------------------------------------------------------------------------
// RovingFocusGroup
// -----------------------------------------------------------------------------------------------

const GROUP_NAME = 'RovingFocusGroup';

type ItemData = { id: string; focusable: boolean; active: boolean };
const [RovingFocusCollection, useCollection, createCollectionScope] =
  Collection.createCollection<HTMLSpanElement, ItemData>(GROUP_NAME);

type ScopedProps<P> = P & { __scopeRovingFocusGroup?: Context.Scope };
const [createRovingFocusGroupContext, createRovingFocusGroupScope] =
  Context.createContextScope(GROUP_NAME, [createCollectionScope]);

type Orientation = React.AriaAttributes['aria-orientation'];
type Direction = 'ltr' | 'rtl';

interface RovingFocusGroupOptions {
  /**
   * The orientation of the group.
   * Mainly so arrow navigation is done accordingly (left & right vs. up & down)
   */
  orientation?: Orientation;
  /**
   * The direction of navigation between items.
   */
  dir?: Direction;
  /**
   * Whether keyboard navigation should loop around
   * @defaultValue false
   */
  loop?: boolean;
}

type RovingContextValue = RovingFocusGroupOptions & {
  currentTabStopId: string | null;
  onItemFocus(tabStopId: string): void;
  onItemShiftTab(): void;
  onFocusableItemAdd(): void;
  onFocusableItemRemove(): void;
};

const [RovingFocusProvider, useRovingFocusContext] =
  createRovingFocusGroupContext<RovingContextValue>(GROUP_NAME);

type RovingFocusGroupElement = RovingFocusGroupImplElement;
type RovingFocusGroupProps = RovingFocusGroupImplProps;

const RovingFocusGroup = React.forwardRef<
  RovingFocusGroupElement,
  RovingFocusGroupProps
>((props: ScopedProps<RovingFocusGroupProps>, forwardedRef) => (
  <RovingFocusCollection.Provider scope={props.__scopeRovingFocusGroup}>
    <RovingFocusCollection.Slot scope={props.__scopeRovingFocusGroup}>
      <RovingFocusGroupImpl {...props} ref={forwardedRef} />
    </RovingFocusCollection.Slot>
  </RovingFocusCollection.Provider>
));

RovingFocusGroup.displayName = GROUP_NAME;

// -----------------------------------------------------------------------------------------------

type RovingFocusGroupImplElement = React.ElementRef<typeof Primitive.div>;
type PrimitiveDivProps = React.ComponentPropsWithoutRef<typeof Primitive.div>;
interface RovingFocusGroupImplProps
  extends Omit<PrimitiveDivProps, 'dir'>,
    RovingFocusGroupOptions {
  currentTabStopId?: string | null;
  defaultCurrentTabStopId?: string;
  onCurrentTabStopIdChange?: (tabStopId: string | null) => void;
  onEntryFocus?: (event: Event) => void;
}

const RovingFocusGroupImpl = React.forwardRef<
  RovingFocusGroupImplElement,
  RovingFocusGroupImplProps
>((props: ScopedProps<RovingFocusGroupImplProps>, forwardedRef) => {
  const {
    __scopeRovingFocusGroup: scopeRovingFocusGroup,
    orientation,
    loop = false,
    dir,
    currentTabStopId: currentTabStopIdProp,
    defaultCurrentTabStopId,
    onCurrentTabStopIdChange,
    onEntryFocus,
    ...groupProps
  } = props;
  const ref = React.useRef<RovingFocusGroupImplElement>(null);
  const composedRefs = useComposedRefs(forwardedRef, ref);
  const direction = Direction.useDirection(dir);
  const [currentTabStopId, setCurrentTabStopId] = useControllableState({
    prop: currentTabStopIdProp,
    defaultProp: defaultCurrentTabStopId ?? null,
    onChange: onCurrentTabStopIdChange,
  });
  const [isTabbingBackOut, setIsTabbingBackOut] = React.useState(false);
  const handleEntryFocus = useCallbackRef(onEntryFocus);
  const getItems = useCollection(scopeRovingFocusGroup);
  const isClickFocusRef = React.useRef(false);
  const [focusableItemsCount, setFocusableItemsCount] = React.useState(0);

  React.useEffect(() => {
    const node = ref.current;

    if (node) {
      node.addEventListener(ENTRY_FOCUS, handleEntryFocus);
      return () => node.removeEventListener(ENTRY_FOCUS, handleEntryFocus);
    }

    return;
  }, [handleEntryFocus]);

  return (
    <RovingFocusProvider
      currentTabStopId={currentTabStopId}
      dir={direction}
      loop={loop}
      orientation={orientation}
      scope={scopeRovingFocusGroup}
      onItemShiftTab={React.useCallback(() => setIsTabbingBackOut(true), [])}
      onFocusableItemAdd={React.useCallback(
        () => setFocusableItemsCount((prevCount) => prevCount + 1),
        [],
      )}
      onFocusableItemRemove={React.useCallback(
        () => setFocusableItemsCount((prevCount) => prevCount - 1),
        [],
      )}
      onItemFocus={React.useCallback(
        (tabStopId) => setCurrentTabStopId(tabStopId),
        [setCurrentTabStopId],
      )}
    >
      <Primitive.div
        data-orientation={orientation}
        tabIndex={isTabbingBackOut || focusableItemsCount === 0 ? -1 : 0}
        {...groupProps}
        ref={composedRefs}
        style={{ outline: 'none', ...props.style }}
        onBlur={composeEventHandlers(props.onBlur, () => {
          setIsTabbingBackOut(false);
        })}
        onFocus={composeEventHandlers(props.onFocus, (event) => {
          // We normally wouldn't need this check, because we already check
          // that the focus is on the current target and not bubbling to it.
          // We do this because Safari doesn't focus buttons when clicked, and
          // instead, the wrapper will get focused and not through a bubbling event.
          const isKeyboardFocus = !isClickFocusRef.current;

          if (
            event.target === event.currentTarget &&
            isKeyboardFocus &&
            !isTabbingBackOut
          ) {
            const entryFocusEvent = new CustomEvent(ENTRY_FOCUS, EVENT_OPTIONS);
            event.currentTarget.dispatchEvent(entryFocusEvent);

            if (!entryFocusEvent.defaultPrevented) {
              const items = getItems().filter((item) => item.focusable);
              const activeItem = items.find((item) => item.active);
              const currentItem = items.find(
                (item) => item.id === currentTabStopId,
              );
              // Introducing lint rule banning type assertions
              // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
              const candidateItems = [activeItem, currentItem, ...items].filter(
                Boolean,
              ) as typeof items;
              const candidateNodes = candidateItems.map(
                (item) => item.ref.current,
              );
              focusFirst(candidateNodes);
            }
          }

          isClickFocusRef.current = false;
        })}
        onMouseDown={composeEventHandlers(props.onMouseDown, () => {
          isClickFocusRef.current = true;
        })}
      />
    </RovingFocusProvider>
  );
});

RovingFocusGroupImpl.displayName = 'RovingFocusGroupImpl';

// -----------------------------------------------------------------------------------------------
// RovingFocusGroupItem
// -----------------------------------------------------------------------------------------------

const ITEM_NAME = 'RovingFocusGroupItem';

type RovingFocusItemElement = React.ElementRef<typeof Primitive.span>;
type PrimitiveSpanProps = React.ComponentPropsWithoutRef<typeof Primitive.span>;
interface RovingFocusItemProps extends PrimitiveSpanProps {
  tabStopId?: string;
  focusable?: boolean;
  active?: boolean;
}

const RovingFocusGroupItem = React.forwardRef<
  RovingFocusItemElement,
  RovingFocusItemProps
>((props: ScopedProps<RovingFocusItemProps>, forwardedRef) => {
  const {
    __scopeRovingFocusGroup: scopeRovingFocusGroup,
    focusable = true,
    active = false,
    tabStopId,
    ...itemProps
  } = props;
  const autoId = React.useId();
  const id = tabStopId || autoId;
  const context = useRovingFocusContext(ITEM_NAME, scopeRovingFocusGroup);
  const isCurrentTabStop = context.currentTabStopId === id;
  const getItems = useCollection(scopeRovingFocusGroup);

  React.useEffect(() => {
    if (focusable) {
      context.onFocusableItemAdd();
      return () => context.onFocusableItemRemove();
    }

    return;
  }, [focusable, context]);

  return (
    <RovingFocusCollection.ItemSlot
      active={active}
      focusable={focusable}
      id={id}
      scope={scopeRovingFocusGroup}
    >
      <Primitive.span
        data-orientation={context.orientation}
        // Enforce tabIndex={-1} for non-focusable items (1)
        tabIndex={focusable && isCurrentTabStop ? 0 : -1}
        {...itemProps}
        ref={forwardedRef}
        onFocus={composeEventHandlers(props.onFocus, () =>
          context.onItemFocus(id),
        )}
        onKeyDown={composeEventHandlers(props.onKeyDown, (event) => {
          if (event.key === 'Tab' && event.shiftKey) {
            context.onItemShiftTab();
            return;
          }

          // Allow roving focus even when row contents are focused (2)
          // Original implementation:
          // if (event.target !== event.currentTarget) {
          //   return;
          // }

          const focusIntent = getFocusIntent(
            event,
            context.orientation,
            context.dir,
          );

          if (focusIntent !== undefined) {
            event.preventDefault();
            const items = getItems().filter((item) => item.focusable);
            let candidateNodes: (HTMLSpanElement | null | undefined)[] =
              items.map((item) => item.ref.current);

            if (focusIntent === 'last') {
              candidateNodes.reverse();
            } else if (focusIntent === 'prev' || focusIntent === 'next') {
              if (focusIntent === 'prev') {
                candidateNodes.reverse();
              }

              const currentIndex = candidateNodes.indexOf(event.currentTarget);
              candidateNodes = context.loop
                ? wrapArray(candidateNodes, currentIndex + 1)
                : candidateNodes.slice(currentIndex + 1);
            }

            /**
             * Imperative focus during keydown is risky so we prevent React's batching updates
             * to avoid potential bugs. See: https://github.com/facebook/react/issues/20332
             */
            setTimeout(() => focusFirst(candidateNodes));
          }
        })}
        onMouseDown={composeEventHandlers(props.onMouseDown, () => {
          // Don't prevent default onMouseDown to allow text selection (3)
          // Original implementation:
          // if (!focusable) event.preventDefault();

          // Safari doesn't focus a button when clicked so we run our logic on mousedown also
          if (focusable) {
            context.onItemFocus(id);
          }
        })}
      />
    </RovingFocusCollection.ItemSlot>
  );
});

RovingFocusGroupItem.displayName = ITEM_NAME;

/* -----------------------------------------------------------------------------------------------*/

// prettier-ignore
const MAP_KEY_TO_FOCUS_INTENT: Record<string, FocusIntent> = {
  ArrowLeft: 'prev', ArrowUp: 'prev',
  ArrowRight: 'next', ArrowDown: 'next',
  PageUp: 'first', Home: 'first',
  PageDown: 'last', End: 'last',
};

function getDirectionAwareKey(key: string, dir?: Direction) {
  if (dir !== 'rtl') {
    return key;
  }

  if (key === 'ArrowLeft') {
    return 'ArrowRight';
  }

  if (key === 'ArrowRight') {
    return 'ArrowLeft';
  }

  return key;
}

type FocusIntent = 'first' | 'last' | 'prev' | 'next';

function getFocusIntent(
  event: React.KeyboardEvent,
  orientation?: Orientation,
  dir?: Direction,
) {
  const key = getDirectionAwareKey(event.key, dir);
  if (orientation === 'vertical' && ['ArrowLeft', 'ArrowRight'].includes(key)) {
    return undefined;
  }

  if (orientation === 'horizontal' && ['ArrowUp', 'ArrowDown'].includes(key)) {
    return undefined;
  }

  return MAP_KEY_TO_FOCUS_INTENT[key];
}

function focusFirst(candidates: (HTMLElement | null | undefined)[]) {
  const PREVIOUSLY_FOCUSED_ELEMENT = document.activeElement;
  for (const candidate of candidates) {
    if (!candidate) {
      return;
    }

    // if focus is already where we want to go, we don't want to keep going through the candidates
    if (candidate === PREVIOUSLY_FOCUSED_ELEMENT) {
      return;
    }

    candidate?.focus();
    if (document.activeElement !== PREVIOUSLY_FOCUSED_ELEMENT) {
      return;
    }
  }
}

/**
 * Wraps an array around itself at a given start index
 * Example: `wrapArray(['a', 'b', 'c', 'd'], 2) === ['c', 'd', 'a', 'b']`
 */
const wrapArray = <T,>(array: T[], startIndex: number) =>
  array.map((_, index) => array[(startIndex + index) % array.length]);

const Root = RovingFocusGroup;
const Item = RovingFocusGroupItem;

export {
  createRovingFocusGroupScope,
  Item,
  Root,
  RovingFocusGroup,
  RovingFocusGroupItem,
};
export type { RovingFocusGroupProps, RovingFocusItemProps };
